type ShareSegment = {
  id: number | string;
  name: string;
  color: string;
  percent: number;
};

type ShareSummaryProps = {
  label: string;
  value: string;
  segments: ShareSegment[];
  /** Rendered under the strip when the panel wants to name the leading series. */
  caption?: string;
};

/**
 * The part-to-whole cue for a composition panel: the total, then one 100% strip split by
 * series. It replaces the donut those panels used to carry — the ranked rows underneath
 * already answer "how much" per series, and comparing bar lengths beats comparing angles.
 */
export const ShareSummary = ({ label, value, segments, caption }: ShareSummaryProps) => (
  <div className="min-w-0">
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--app-muted-text)]">
        {label}
      </p>
      <p className="text-2xl font-semibold tabular-nums tracking-[-0.04em] text-[var(--app-text)]">
        {value}
      </p>
    </div>

    <div className="mt-3 flex h-3 w-full gap-0.5" role="presentation">
      {segments.length === 0 ? (
        <span className="h-full w-full rounded-full bg-[var(--app-surface-soft-2)]" />
      ) : (
        segments.map((segment) => (
          <span
            key={`share-${segment.id}`}
            title={`${segment.name} · ${Math.round(segment.percent)}%`}
            // Percentages as flex-basis, not width: the gaps then eat proportionally
            // into the segments instead of pushing the strip past 100%.
            style={{ flex: `0 1 ${segment.percent}%`, background: segment.color }}
            className="h-full min-w-1 rounded-full"
          />
        ))
      )}
    </div>

    {caption ? (
      <p className="mt-2 text-xs text-[var(--app-text-secondary)]">{caption}</p>
    ) : null}
  </div>
);
